import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Checkbox,
  Select,
  InputNumber,
  Radio,
  Space,
  message,
  Alert,
  Typography,
  Divider,
  Row,
  Col,
} from 'antd';
import {
  BellOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  createReminder,
  ReminderMethod,
  ReminderRepeatType,
  CreateReminderRequest,
} from '../../../../services/reminder';
import { getStaff, User } from '../../../../services/user';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

interface SetReminderModalProps {
  visible: boolean;
  targetType: 'case' | 'lead';
  targetId: number;
  onCancel: () => void;
  onSuccess: () => void;
}

// Weekday options for weekly repeat
const WEEKDAY_OPTIONS = [
  { label: '周一', value: 1 },
  { label: '周二', value: 2 },
  { label: '周三', value: 3 },
  { label: '周四', value: 4 },
  { label: '周五', value: 5 },
  { label: '周六', value: 6 },
  { label: '周日', value: 0 },
];

const SetReminderModal: React.FC<SetReminderModalProps> = ({
  visible,
  targetType,
  targetId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [staffUsers, setStaffUsers] = useState<User[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [repeatType, setRepeatType] = useState<ReminderRepeatType>(ReminderRepeatType.NONE);

  // Load staff users for recipients
  const loadStaffUsers = async () => {
    setLoadingStaff(true);
    try {
      const users = await getStaff({ limit: 1000 });
      setStaffUsers(users);
    } catch (error: any) {
      console.error('Failed to load staff users:', error);
      message.error('加载用户列表失败');
    } finally {
      setLoadingStaff(false);
    }
  };

  // Load data when modal opens
  useEffect(() => {
    if (visible) {
      loadStaffUsers();
      // Reset form and state
      form.resetFields();
      setRepeatType(ReminderRepeatType.NONE);
    }
  }, [visible, form]);

  // Handle repeat type change
  const handleRepeatTypeChange = (value: ReminderRepeatType) => {
    setRepeatType(value);
    // Clear repeat config when changing type
    form.setFieldsValue({ repeat_config: undefined });
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Prepare repeat config based on repeat type
      let repeatConfig: Record<string, any> | undefined;
      
      if (repeatType === ReminderRepeatType.DAILY) {
        // No additional config needed for daily
        repeatConfig = {};
      } else if (repeatType === ReminderRepeatType.WEEKLY) {
        repeatConfig = {
          weekdays: values.repeat_config?.weekdays || [],
        };
      } else if (repeatType === ReminderRepeatType.MONTHLY) {
        repeatConfig = {
          day_of_month: values.repeat_config?.day_of_month || 1,
        };
      } else if (repeatType === ReminderRepeatType.CUSTOM) {
        repeatConfig = {
          interval_days: values.repeat_config?.interval_days || 1,
        };
      }

      // Prepare reminder data
      const reminderData: CreateReminderRequest = {
        target_type: targetType,
        target_id: targetId,
        title: values.title,
        content: values.content,
        remind_time: values.remind_time.format('YYYY-MM-DD HH:mm:ss'),
        remind_methods: values.remind_methods,
        recipients: values.recipients,
        repeat_type: repeatType,
        repeat_config: repeatType !== ReminderRepeatType.NONE ? repeatConfig : undefined,
      };

      await createReminder(reminderData);
      message.success('设置提醒成功');
      
      // Reset form
      form.resetFields();
      setRepeatType(ReminderRepeatType.NONE);
      
      // Notify parent
      onSuccess();
    } catch (error: any) {
      console.error('Failed to create reminder:', error);
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error('设置提醒失败：' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    setRepeatType(ReminderRepeatType.NONE);
    onCancel();
  };

  // Disable past dates
  const disabledDate = (current: Dayjs) => {
    return current && current < dayjs().startOf('day');
  };

  // Render repeat config fields based on repeat type
  const renderRepeatConfig = () => {
    switch (repeatType) {
      case ReminderRepeatType.DAILY:
        return (
          <Alert
            message="每天重复"
            description="提醒将在每天的指定时间重复发送"
            type="info"
            showIcon
          />
        );

      case ReminderRepeatType.WEEKLY:
        return (
          <Form.Item
            name={['repeat_config', 'weekdays']}
            label="重复日期"
            rules={[
              { required: true, message: '请选择至少一个星期' },
            ]}
          >
            <Checkbox.Group options={WEEKDAY_OPTIONS} />
          </Form.Item>
        );

      case ReminderRepeatType.MONTHLY:
        return (
          <Form.Item
            name={['repeat_config', 'day_of_month']}
            label="每月第几天"
            rules={[
              { required: true, message: '请输入日期' },
            ]}
            initialValue={1}
          >
            <InputNumber
              min={1}
              max={31}
              placeholder="请输入1-31"
              style={{ width: '100%' }}
              addonAfter="日"
            />
          </Form.Item>
        );

      case ReminderRepeatType.CUSTOM:
        return (
          <Form.Item
            name={['repeat_config', 'interval_days']}
            label="间隔天数"
            rules={[
              { required: true, message: '请输入间隔天数' },
            ]}
            initialValue={1}
          >
            <InputNumber
              min={1}
              max={365}
              placeholder="请输入间隔天数"
              style={{ width: '100%' }}
              addonAfter="天"
            />
          </Form.Item>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <Space>
          <BellOutlined />
          <span>设置提醒</span>
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      okText="确定"
      cancelText="取消"
      width={700}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          remind_methods: [ReminderMethod.SYSTEM],
          repeat_type: ReminderRepeatType.NONE,
        }}
      >
        <Form.Item
          name="title"
          label="提醒标题"
          rules={[
            { required: true, message: '请输入提醒标题' },
            { max: 100, message: '标题不能超过100个字符' },
          ]}
        >
          <Input
            placeholder="请输入提醒标题，例如：开庭提醒、材料提交截止"
            showCount
            maxLength={100}
          />
        </Form.Item>

        <Form.Item
          name="remind_time"
          label="提醒时间"
          rules={[
            { required: true, message: '请选择提醒时间' },
          ]}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder="请选择提醒时间"
            style={{ width: '100%' }}
            disabledDate={disabledDate}
            showNow
          />
        </Form.Item>

        <Form.Item
          name="remind_methods"
          label="提醒方式"
          rules={[
            { required: true, message: '请选择至少一种提醒方式' },
          ]}
        >
          <Checkbox.Group style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Checkbox value={ReminderMethod.SYSTEM}>
                  <Space direction="vertical" size={0}>
                    <Text strong>系统通知</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      在系统内显示通知消息
                    </Text>
                  </Space>
                </Checkbox>
              </Col>
              <Col span={12}>
                <Checkbox value={ReminderMethod.WECHAT}>
                  <Space direction="vertical" size={0}>
                    <Text strong>企业微信</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      通过企业微信发送消息
                    </Text>
                  </Space>
                </Checkbox>
              </Col>
              <Col span={12}>
                <Checkbox value={ReminderMethod.SMS}>
                  <Space direction="vertical" size={0}>
                    <Text strong>短信</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      发送短信到手机
                    </Text>
                  </Space>
                </Checkbox>
              </Col>
              <Col span={12}>
                <Checkbox value={ReminderMethod.EMAIL}>
                  <Space direction="vertical" size={0}>
                    <Text strong>邮件</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      发送邮件通知
                    </Text>
                  </Space>
                </Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Form.Item>

        <Form.Item
          name="recipients"
          label="提醒对象"
          rules={[
            { required: true, message: '请选择至少一位提醒对象' },
          ]}
        >
          <Select
            mode="multiple"
            placeholder="请选择提醒对象（可多选）"
            loading={loadingStaff}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={staffUsers.map(user => ({
              label: `${user.real_name || user.username}${user.department ? ` (${user.department})` : ''}`,
              value: user.id,
            }))}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item
          name="content"
          label="提醒内容"
          tooltip="可选，补充说明提醒的详细信息"
        >
          <TextArea
            rows={4}
            placeholder="请输入提醒内容（可选）"
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Divider />

        <Form.Item
          label={
            <Space>
              <ClockCircleOutlined />
              <span>重复设置</span>
            </Space>
          }
        >
          <Radio.Group
            value={repeatType}
            onChange={(e) => handleRepeatTypeChange(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio value={ReminderRepeatType.NONE}>
                不重复 - 仅提醒一次
              </Radio>
              <Radio value={ReminderRepeatType.DAILY}>
                每天重复 - 每天在指定时间提醒
              </Radio>
              <Radio value={ReminderRepeatType.WEEKLY}>
                每周重复 - 每周指定日期提醒
              </Radio>
              <Radio value={ReminderRepeatType.MONTHLY}>
                每月重复 - 每月指定日期提醒
              </Radio>
              <Radio value={ReminderRepeatType.CUSTOM}>
                自定义间隔 - 按指定天数间隔提醒
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {repeatType !== ReminderRepeatType.NONE && (
          <div style={{ marginTop: 16, marginLeft: 24 }}>
            {renderRepeatConfig()}
          </div>
        )}

        <Alert
          message="提示"
          description={
            repeatType === ReminderRepeatType.NONE
              ? '提醒将在指定时间发送一次。'
              : '重复提醒将根据设置的规则自动创建，直到手动取消。'
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Form>
    </Modal>
  );
};

export default SetReminderModal;
